/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { PriceBookRestService } from '@gh/builder-shared-data';
import { getRouteParam, getRouteStaticData, InitViewService } from '@gh/common-shared';
import { EntityVersion, ID } from '@gh/common-shared-data';
import { GridLayoutService } from '@gh/core-ui-grid';
import { map$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import {
  PbLinkCcElement,
  pbLinkCsmCcElementsFromDto,
  pbLinkCsmGetResponseFromDto,
  PbLinkEntryPoint,
  PbLinkGetResponse,
  pbLinkJstCcElementsFromDto,
  pbLinkJstGetResponseFromDto,
  PbLinkMappingContext,
  PbLinkSaveRequest,
  pbLinkSaveRequestToDto,
  PbLinkSaveResponse,
} from './price-book-link.model';

@Injectable()
export class PriceBookLinkBusinessService implements InitViewService {
  constructor(private priceBookRest: PriceBookRestService,
              private gridLayoutService: GridLayoutService) {

  }

  initView(snapshot: ActivatedRouteSnapshot): any {
    const priceBookId = getRouteParam(snapshot, 'priceBookId');
    const entryPoint = getRouteStaticData(snapshot)['entryPoint'];

    return {
      ccGridLayout: this.gridLayoutService.get('pbLinkCostCenters'),
      elementsGridLayout: this.gridLayoutService.get('pbLinkElements'),
      linksGridLayout: this.gridLayoutService.get({
        [PbLinkEntryPoint.Jst]: 'pbLinkJst',
        [PbLinkEntryPoint.Csm]: 'pbLinkCsm',
      }[entryPoint]),
      init: this.getLinks(entryPoint, priceBookId),
    };
  }

  getLinks(entryPoint: PbLinkEntryPoint, priceBookId: ID): Observable<PbLinkGetResponse> {
    return entryPoint === PbLinkEntryPoint.Jst ?
      this.priceBookRest.getJstLinks(priceBookId).pipe(map$(pbLinkJstGetResponseFromDto))
      : this.priceBookRest.getCsmLinks(priceBookId).pipe(map$(pbLinkCsmGetResponseFromDto));
  }

  getCcElements(entryPoint: PbLinkEntryPoint,
                priceBookId: ID,
                costCenterId: ID,
                version: EntityVersion,
                mappingContext: PbLinkMappingContext): Observable<PbLinkCcElement[]> {
    return this.priceBookRest.findRelatedCcStructures({ priceBookId, costCenterId, version, items: [] }).pipe(
      map$((elements) => entryPoint === PbLinkEntryPoint.Jst ?
        pbLinkJstCcElementsFromDto(elements, mappingContext) : pbLinkCsmCcElementsFromDto(elements, mappingContext)));
  }

  saveLinks(entryPoint: PbLinkEntryPoint,
            priceBookId: ID,
            request: PbLinkSaveRequest,
            mappingContext: PbLinkMappingContext): Observable<PbLinkSaveResponse> {
    return entryPoint === PbLinkEntryPoint.Jst ?
      this.priceBookRest.saveJstLinks(priceBookId, pbLinkSaveRequestToDto(request, mappingContext))
      : this.priceBookRest.saveCsmLinks(priceBookId, pbLinkSaveRequestToDto(request, mappingContext));
  }
}
